import { isNativePlatform } from './platform';

/**
 * Access tokens live in memory only, on both platforms — a page refresh
 * re-derives one from the refresh token. This means an XSS payload cannot
 * lift a long-lived credential from storage. See docs/05-security.md.
 */
let accessToken: string | null = null;

export const tokenStore = {
  get: (): string | null => accessToken,
  set: (token: string | null): void => {
    accessToken = token;
  },
};

const REFRESH_KEY = 'hxiq.refresh';

/**
 * Web keeps the refresh token in an httpOnly cookie the browser sends
 * automatically, so these are no-ops there. Native has no usable cookie jar,
 * so the token is persisted locally (and @capacitor/preferences replaces
 * localStorage here in Phase 6).
 */
export const refreshTokenStore = {
  get(): string | undefined {
    if (!isNativePlatform()) return undefined;
    return localStorage.getItem(REFRESH_KEY) ?? undefined;
  },
  set(token: string | undefined): void {
    if (!isNativePlatform()) return;
    if (token) localStorage.setItem(REFRESH_KEY, token);
    else localStorage.removeItem(REFRESH_KEY);
  },
};

const SESSION_HINT_KEY = 'hxiq.hasSession';

/**
 * A non-sensitive marker recording that this browser has logged in before.
 *
 * The refresh token itself is httpOnly on web, so JavaScript cannot tell
 * whether a session exists — without a hint, every anonymous visitor to the
 * landing page triggers a refresh request that answers 401. That is a wasted
 * round trip on the marketing surface and a console error on first paint.
 *
 * This holds no credential and grants no access; a forged value costs at most
 * one refresh attempt that fails exactly as it does today.
 */
export const sessionHint = {
  exists(): boolean {
    try {
      return localStorage.getItem(SESSION_HINT_KEY) === '1';
    } catch {
      // Storage is unavailable (private mode, embedded WebView). Assume a
      // session might exist so a real one is never stranded.
      return true;
    }
  },
  set(exists: boolean): void {
    try {
      if (exists) localStorage.setItem(SESSION_HINT_KEY, '1');
      else localStorage.removeItem(SESSION_HINT_KEY);
    } catch {
      /* best effort */
    }
  },
};

// ── Platform console ────────────────────────────────────────────────────────
// A platform administrator keeps the console open while inside a shop, so the
// two sessions are stored apart: this is the console's, `tokenStore` above is
// the shop's. See docs/adr/0011-platform-administration.md.

let platformAccessToken: string | null = null;

export const platformTokenStore = {
  get: (): string | null => platformAccessToken,
  set: (token: string | null): void => {
    platformAccessToken = token;
  },
};

const PLATFORM_REFRESH_KEY = 'hxiq.platform.refresh';

export const platformRefreshTokenStore = {
  get(): string | undefined {
    if (!isNativePlatform()) return undefined;
    return localStorage.getItem(PLATFORM_REFRESH_KEY) ?? undefined;
  },
  set(token: string | undefined): void {
    if (!isNativePlatform()) return;
    if (token) localStorage.setItem(PLATFORM_REFRESH_KEY, token);
    else localStorage.removeItem(PLATFORM_REFRESH_KEY);
  },
};

const PLATFORM_SESSION_HINT_KEY = 'hxiq.platform.hasSession';

export const platformSessionHint = {
  exists(): boolean {
    try {
      return localStorage.getItem(PLATFORM_SESSION_HINT_KEY) === '1';
    } catch {
      return true;
    }
  },
  set(exists: boolean): void {
    try {
      if (exists) localStorage.setItem(PLATFORM_SESSION_HINT_KEY, '1');
      else localStorage.removeItem(PLATFORM_SESSION_HINT_KEY);
    } catch {
      /* best effort */
    }
  },
};

const IMPERSONATION_KEY = 'hxiq.actingAs';

/**
 * Whether the shop session in `tokenStore` was opened from the console. Such
 * a session has no refresh token: when it expires, or the page reloads, the
 * app goes back to the console rather than to the shop's login screen. The
 * marker survives a reload so the app knows where to go.
 */
export const impersonation = {
  active(): boolean {
    try {
      return sessionStorage.getItem(IMPERSONATION_KEY) === '1';
    } catch {
      return false;
    }
  },
  set(active: boolean): void {
    try {
      if (active) sessionStorage.setItem(IMPERSONATION_KEY, '1');
      else sessionStorage.removeItem(IMPERSONATION_KEY);
    } catch {
      /* best effort */
    }
  },
};

// ── Customer portal ─────────────────────────────────────────────────────────
// A customer's contact (ADR-0013). Its own session, apart from the shop's and
// the console's, so one browser can hold any of them without confusion.

let portalAccessToken: string | null = null;

export const portalTokenStore = {
  get: (): string | null => portalAccessToken,
  set: (token: string | null): void => {
    portalAccessToken = token;
  },
};

const PORTAL_REFRESH_KEY = 'hxiq.portal.refresh';

export const portalRefreshTokenStore = {
  get(): string | undefined {
    if (!isNativePlatform()) return undefined;
    return localStorage.getItem(PORTAL_REFRESH_KEY) ?? undefined;
  },
  set(token: string | undefined): void {
    if (!isNativePlatform()) return;
    if (token) localStorage.setItem(PORTAL_REFRESH_KEY, token);
    else localStorage.removeItem(PORTAL_REFRESH_KEY);
  },
};

const PORTAL_SESSION_HINT_KEY = 'hxiq.portal.hasSession';

export const portalSessionHint = {
  exists(): boolean {
    try {
      return localStorage.getItem(PORTAL_SESSION_HINT_KEY) === '1';
    } catch {
      return true;
    }
  },
  set(exists: boolean): void {
    try {
      if (exists) localStorage.setItem(PORTAL_SESSION_HINT_KEY, '1');
      else localStorage.removeItem(PORTAL_SESSION_HINT_KEY);
    } catch {
      /* best effort */
    }
  },
};
